import {
  CategorySchema,
  ImportResultSchema,
  ProductPageSchema,
  ProductSchema,
  type CountStockRequest,
  type CreateCategoryRequest,
  type CreateProductRequest,
  type UpdateCategoryRequest,
  type UpdateProductRequest,
} from '@deli/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as z from 'zod';
import { getJson, sendFile, sendJson } from './client.js';

export interface ProductFilter {
  search?: string;
  categoryId?: string;
  active?: boolean;
  offset?: number;
}

export const PRODUCT_PAGE = 50;

export const useCategories = () =>
  useQuery({
    queryKey: ['catalogue', 'categories'],
    queryFn: ({ signal }) =>
      getJson('/api/v1/catalogue/categories', z.array(CategorySchema), { signal }),
  });

export const useProducts = (filter: ProductFilter) =>
  useQuery({
    queryKey: ['catalogue', 'products', filter],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: String(PRODUCT_PAGE) });
      if (filter.search) params.set('search', filter.search);
      if (filter.categoryId) params.set('categoryId', filter.categoryId);
      if (filter.active !== undefined) params.set('active', String(filter.active));
      if (filter.offset) params.set('offset', String(filter.offset));
      return getJson(`/api/v1/catalogue/products?${params}`, ProductPageSchema, { signal });
    },
    placeholderData: keepPreviousData,
  });

function useCatalogueMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ['catalogue'] }),
  });
}

export const useUpdateProduct = () =>
  useCatalogueMutation(({ id, ...body }: UpdateProductRequest & { id: string }) =>
    sendJson('PATCH', `/api/v1/catalogue/products/${id}`, body, ProductSchema),
  );

export const useCountStock = () =>
  useCatalogueMutation(({ id, ...body }: CountStockRequest & { id: string }) =>
    sendJson('PUT', `/api/v1/catalogue/products/${id}/stock`, body, ProductSchema),
  );

export const useImportCatalogue = () =>
  useCatalogueMutation((body: { csv: string; dryRun: boolean }) =>
    sendJson('POST', '/api/v1/catalogue/import', body, ImportResultSchema),
  );

export const useCreateProduct = () =>
  useCatalogueMutation((body: CreateProductRequest) =>
    sendJson('POST', '/api/v1/catalogue/products', body, ProductSchema),
  );

export const useCreateCategory = () =>
  useCatalogueMutation((body: CreateCategoryRequest) =>
    sendJson('POST', '/api/v1/catalogue/categories', body, CategorySchema),
  );

export const useUpdateCategory = () =>
  useCatalogueMutation(({ id, ...body }: UpdateCategoryRequest & { id: string }) =>
    sendJson('PATCH', `/api/v1/catalogue/categories/${id}`, body, CategorySchema),
  );

export const useDeleteCategory = () =>
  useCatalogueMutation((id: string) =>
    sendJson('DELETE', `/api/v1/catalogue/categories/${id}`, undefined, null),
  );

// ── Product images (catalogue.edit) ───────────────────────────────────────────────────────

const images = (productId: string) => `/api/v1/catalogue/products/${productId}/images`;

export const uploadHero = (productId: string, file: Blob) =>
  sendFile('PUT', `${images(productId)}/hero`, file, ProductSchema);

export const uploadExtra = (productId: string, file: Blob) =>
  sendFile('POST', images(productId), file, ProductSchema);

export const useSetHero = () =>
  useCatalogueMutation(({ productId, file }: { productId: string; file: Blob }) =>
    uploadHero(productId, file),
  );

/** Several files, one after another (keeps their order). */
export const useAddImages = () =>
  useCatalogueMutation(async ({ productId, files }: { productId: string; files: Blob[] }) => {
    for (const file of files) await uploadExtra(productId, file);
  });

export const useRemoveImage = () =>
  useCatalogueMutation(({ productId, imageId }: { productId: string; imageId: string }) =>
    sendJson('DELETE', `${images(productId)}/${imageId}`, undefined, ProductSchema),
  );

export const useReorderImages = () =>
  useCatalogueMutation(({ productId, imageIds }: { productId: string; imageIds: string[] }) =>
    sendJson('PUT', `${images(productId)}/order`, { imageIds }, ProductSchema),
  );
